import "./style.scss";

import { Button, CircularProgress, Tooltip } from "@mui/material";
import { NotAllow } from "assets/img/svg/notAllow";
import SendEmailIcon from "assets/img/svg/sendEmail";
import ModalBox from "components/Modal";
import { isManagementBankInfoComplete } from "helpers/paymentExportUtils";
import { SmallBody } from "helpers/TypographyComponents";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { ITermBeneficiar, TermState } from "store/managementStore";
import { setLastSentAtTerms } from "store/managementStore";
import { EPaymentResourceType } from "store/paymentSlice";
import { sendBulkMailWithStatement } from "store/statementsStore";
import type { RootState } from "store/store";
import { useAppDispatch, useAppSelector } from "store/store";
import { PeriodStatus } from "store/types";
import type { EStatementType } from "types";

import Document from "../../../assets/img/svg/documentPurple";
import variables from "../../../assets/styles/_variables.module.scss";
import MessageBlock from "../MessageBlock";
import ModalBase from "../ModalBase";
import type { INewPaymentData } from "../PaymentModal";
import PaymentModal from "../PaymentModal";
import SendEmail from "../SendEmail";
import SendEmailBeneficiar from "../SendEmailBeneficiar";
import StyledButton from "../StyledButton";
import StatementModal from "./ModalContent/Statement";

type TProps = {
    selectedRows: number[];
    tabValue: number;
    showProvisions: () => void;
    closeTerm: () => void;
    allUsePortal: boolean;
    periodStatus?: PeriodStatus;
};

type SelectedRowsHandlerProps = Partial<TermState> & TProps;

export const paymentStatusColors = [
    { name: "notPaid", text: "#B55D5D", background: "#f2bfcd66" },
    { name: "toPay", text: "#AF8442", background: "#f4a02266" },
    { name: "paid", text: "#5D9B62", background: "#98d49e66" },
];

const SelectedRowsHandler = ({
    selectedRows = [],
    tabValue,
    generalTabData = [],
    closeTermsStatus,
    showProvisions,
    closeTerm,
    allUsePortal,
    periodStatus,
}: SelectedRowsHandlerProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { countryFeatures, internationalBankingEnabled } = useCompanySettings();
    const { beneficiaries } = useAppSelector((state) => state.management);
    const isPeriodFuture = periodStatus === PeriodStatus.FUTURE;
    const hasClosedTerms = generalTabData.some(({ id, isClosed }) => selectedRows.includes(id) && isClosed === 1);
    const selectedTerms = generalTabData.filter((item: any) => selectedRows.includes(item.id));
    const selectedBeneficiars = beneficiaries.filter(
        (item: any) => selectedRows.includes(item.id) && !item.customStatementTemplateId,
    );
    const hasCustomStatementsBeneficiars = beneficiaries.filter(
        (beneficiary) => selectedRows.includes(beneficiary.id) && beneficiary.customStatementTemplateId,
    );
    const activePeriod = useSelector((state: RootState) => state.period.activePeriod);
    const [modalType, setModalType] = useState<"payment" | "statement" | "mailBenef" | "mailTerm" | "closeTerm" | "">(
        "",
    );
    const [elementsForPayment, setElementsForPayments] = useState<INewPaymentData[]>([]);

    const handleCloseTerms = () => {
        if (tabValue === 0) {
            closeTerm();
        }
    };

    /**
     * Opens modal for paying beneficiaries (partially or full).
     */
    const handleOpenPaymentModal = () => {
        setModalType("payment");
    };

    const sendEmail = (
        emails: string[],
        subject: string,
        body: string,
        ignore?: EStatementType[],
        subjectByLang?: Record<string, string>,
        bodyByLang?: Record<string, string>,
    ) => {
        if (!activePeriod) return;
        dispatch(
            sendBulkMailWithStatement({
                termId: selectedTerms.map((item: any) => item.id),
                periodId: activePeriod,
                mailCc: emails,
                subject,
                body,
                subjectByLang,
                bodyByLang,
                ignore,
            }),
        );
        dispatch(setLastSentAtTerms(selectedTerms.map((item: any) => item.id)));
        setModalType("");
    };

    const haveNoEmailGeneral = useMemo(() => {
        return selectedTerms.filter((term) => term.email === null || term.email === "");
    }, [selectedTerms]);

    const haveNoEmailBeneficiaries = useMemo(() => {
        return selectedBeneficiars.filter((benef) => benef.email === null || benef.email === "");
    }, [selectedTerms]);

    useEffect(() => {
        const elementsData: INewPaymentData[] = beneficiaries
            .filter((item) => selectedRows.includes(item.id) && !item.underTheMinimum)
            .map((item) => {
                const leftToPay = item?.stageAndCommitted ? item.netToPay - item?.stageAndCommitted : item.netToPay;
                const bankInfoValid = isManagementBankInfoComplete(
                    item,
                    countryFeatures.bankingFields,
                    internationalBankingEnabled,
                );
                return {
                    amount: leftToPay ?? 0,
                    id: item.id,
                    name: item.beneficiaryName,
                    completingPayment: leftToPay < item.netToPay && leftToPay > 0,
                    bankInfoValid,
                    iban: item.iban,
                    underTheMinimum: item.underTheMinimum,
                };
            });
        setElementsForPayments(elementsData.filter((item) => (item.amount ?? 0) > 0));
    }, [selectedRows, internationalBankingEnabled, countryFeatures.bankingFields]);

    return (
        <>
            <ModalBox
                open={modalType === "statement"}
                onClose={() => setModalType("")}
                mainTitle={t("Management.DownloadStatement")}
                subTitle={t("Management.GroupAction")}
            >
                <StatementModal selectedRows={selectedRows} close={() => setModalType("")} tabValue={tabValue} />
            </ModalBox>
            {modalType === "closeTerm" && (
                <ModalBase
                    onClose={() => setModalType("")}
                    open={modalType === "closeTerm"}
                    modalTitle={t("Management.EndContract")}
                >
                    <div className="selected-rows-modal-close">
                        <div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                                {selectedRows.length > 1 ? (
                                    <SmallBody>{t("Management.ConfirmEndContracts")}</SmallBody>
                                ) : (
                                    <SmallBody>
                                        {t("Management.ConfirmEndContract", {
                                            title: selectedTerms[0]?.title,
                                            author: selectedTerms[0]?.author,
                                        })}
                                    </SmallBody>
                                )}
                                <SmallBody color="danger">{t("Management.ConfirmEndContract1")}</SmallBody>
                            </div>
                            {selectedTerms.map((term, i) => {
                                return (
                                    <span key={`close-selected-term-${i}`} className="selected-term">
                                        {term.title} - {term.author}
                                        {term.representative ? ` / ${term.representative}` : ""}
                                    </span>
                                );
                            })}
                            <MessageBlock
                                type="warning"
                                text={t("Management.IrreversibleDecision")}
                                small
                                style={{ marginBlock: "1rem" }}
                            />
                        </div>
                        <StyledButton
                            buttonType="primary"
                            sx={{ marginTop: "8px" }}
                            fullWidth
                            onClick={() => handleCloseTerms()}
                        >
                            {t("ImportData.Confirm")}
                        </StyledButton>
                    </div>
                </ModalBase>
            )}
            <SendEmail
                isModalOpen={modalType === "mailTerm"}
                onClose={() => setModalType("")}
                sendEmail={sendEmail}
                isBulk={selectedTerms.length === 1 ? false : true}
                hasAuthorPortalAccess={allUsePortal}
                haveNoEmail={haveNoEmailGeneral}
                termData={
                    selectedTerms.length === 1
                        ? {
                              beneficiaryId: selectedTerms[0].beneficiarId,
                              author: selectedTerms[0].author,
                              title: selectedTerms[0].title,
                              email: selectedTerms[0].email ?? "",
                              precount: `${selectedTerms[0].precount}`,
                              contributions: selectedTerms[0].contributions,
                          }
                        : undefined
                }
            />
            <SendEmailBeneficiar
                isModalOpen={modalType === "mailBenef"}
                isBulk={selectedBeneficiars.length === 1 ? false : true}
                beneficiaries={selectedBeneficiars}
                hasCustomStatementsBeneficiars={hasCustomStatementsBeneficiars}
                haveNoEmail={haveNoEmailBeneficiaries}
                periodId={activePeriod}
                onClose={() => setModalType("")}
            />
            <ModalBase
                open={modalType === "payment"}
                onClose={() => setModalType("")}
                modalTitle={t("PartialPayment.BulkPaymentModalTitle", {
                    element: t("NavigationBar.Beneficiars").toLowerCase(),
                })}
            >
                <PaymentModal
                    elementsData={elementsForPayment}
                    type={EPaymentResourceType.BENEFICIARY}
                    periodId={activePeriod}
                    handleClose={() => setModalType("")}
                    hasSeveralPayments
                />
            </ModalBase>
            <div className="selected-rows-wrapper">
                <div className="selected-rows-counter">{selectedRows.length}</div>
                {tabValue == 0 && (
                    <Button
                        disabled={hasClosedTerms || isPeriodFuture}
                        onClick={showProvisions}
                        sx={{
                            backgroundColor: "white",
                            color: variables["primary-purple-dark"],
                            textTransform: "none",
                            fontFamily: "Poppins",
                        }}
                    >
                        {t("Management.AddProvisions")}
                    </Button>
                )}
                <Button
                    onClick={() => setModalType("statement")}
                    sx={{
                        backgroundColor: "white",
                        color: variables["primary-purple-dark"],
                        textTransform: "none",
                        gap: "10px",
                        fontFamily: "Poppins",
                    }}
                >
                    <Document />
                    {t("Management.DownloadStatement")}
                </Button>
                <Button
                    disabled={isPeriodFuture}
                    onClick={() => (tabValue == 0 ? setModalType("mailTerm") : setModalType("mailBenef"))}
                    sx={{
                        backgroundColor: "white",
                        color: variables["primary-purple-dark"],
                        textTransform: "none",
                        gap: "10px",
                        fontFamily: "Poppins",
                    }}
                >
                    <span className="send-icon">
                        <SendEmailIcon />
                    </span>
                    {t("Management.SendStatement")}
                </Button>
                {tabValue == 0 && (
                    <Button
                        disabled={hasClosedTerms || isPeriodFuture}
                        sx={{
                            textTransform: "none",
                            gap: "10px",
                            color: "#B55D5D",
                            fontFamily: "Poppins",
                        }}
                        variant="outlined"
                        color="error"
                        onClick={() => setModalType("closeTerm")}
                    >
                        {closeTermsStatus !== "pending" && <NotAllow />}
                        {closeTermsStatus === "pending" ? (
                            <>
                                <CircularProgress size="1em" sx={{ color: "#B55D5D" }} />
                                {t("Management.Loading")}
                            </>
                        ) : selectedRows.length > 1 ? (
                            t("Management.CloseTerms")
                        ) : (
                            t("Management.CloseTerm")
                        )}
                    </Button>
                )}
                {tabValue == 1 && (
                    <Tooltip
                        title={
                            elementsForPayment.length === 0
                                ? "Aucune des lignes sélectionnées ne contient un reste à payer."
                                : ""
                        }
                    >
                        <div>
                            <Button
                                disabled={elementsForPayment.length === 0 || isPeriodFuture}
                                onClick={handleOpenPaymentModal}
                                sx={{
                                    backgroundColor: "white",
                                    color: variables["primary-purple-dark"],
                                    textTransform: "none",
                                    fontFamily: "Poppins",
                                    cursor: "pointer",
                                }}
                            >
                                {t("Management.Payer")}
                            </Button>
                        </div>
                    </Tooltip>
                )}
            </div>
        </>
    );
};

export default SelectedRowsHandler;
